import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { Link } from "react-router-dom";

import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import {
	CmsField,
	CmsGrid,
	cmsInput,
	CmsItem,
	CmsPlacement,
	CmsSection,
	CmsToolbar,
} from "@/components/admin/cms/cms-kit";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import {
	type HomeContent,
	type PromoCard,
	siteApi,
	type TrustItem,
} from "@/lib/site/site-api";

/**
 * Icônes proposées pour la bande de réassurance, avec leur sens.
 *
 * La liste d'origine n'affichait que les identifiants techniques (« bolt »,
 * « pay »), ce qui obligeait à en essayer plusieurs pour trouver le bon.
 */
const ICONS: Array<{ value: string; label: string }> = [
	{ value: "truck", label: "Camion — livraison" },
	{ value: "cart", label: "Panier — commande" },
	{ value: "shield", label: "Bouclier — sécurité" },
	{ value: "refresh", label: "Flèches — retour, échange" },
	{ value: "bolt", label: "Éclair — rapidité" },
	{ value: "pay", label: "Paiement" },
	{ value: "pin", label: "Épingle — localisation" },
	{ value: "heart", label: "Cœur — engagement" },
];

/** Repères de promesse acceptés dans les textes de la réassurance. */
const TOKENS = [
	"{retourJours}",
	"{seuilLivraison}",
	"{livraisonMin}",
	"{livraisonMax}",
	"{villes}",
	"{garantieMois}",
];

/**
/*
 * Les composants « ModeVolet » et « ImageVolet » vivaient ici : ils
 * servaient uniquement le carrousel, qui est passé dans le module
 * Bannières avec son propre formulaire et son propre aperçu.
 */

export function AdminCmsHomePage() {
	const qc = useQueryClient();
	const toast = useToast();
	const { hasAny, isSuperAdmin } = usePermissions();
	const peutEcrire = isSuperAdmin || hasAny("MARKETING.CMS.WRITE");

	const { data, isLoading } = useQuery({
		queryKey: ["home-content"],
		queryFn: siteApi.getHome,
	});

	const [promos, setPromos] = useState<PromoCard[]>([]);
	const [trust, setTrust] = useState<TrustItem[]>([]);
	// Repère l'état chargé pour ne signaler « non enregistré » qu'après une
	// vraie saisie : sans cela, l'alerte s'affiche dès l'ouverture de l'écran.
	const chargement = useRef(true);
	const [dirty, setDirty] = useState(false);

	useEffect(() => {
		if (!data) return;
		chargement.current = true;
		setPromos(data.promoCards ?? []);
		setTrust(data.trustItems ?? []);
		setDirty(false);
		const t = window.setTimeout(() => {
			chargement.current = false;
		}, 0);
		return () => window.clearTimeout(t);
	}, [data]);

	const marquer = () => {
		if (!chargement.current) setDirty(true);
	};

	const save = useMutation({
		mutationFn: () =>
			// `heroSlides` n'est volontairement PAS envoyé : il appartient au
			// module Bannières, qui le reprojette. L'inclure ici réécrirait un
			// carrousel périmé à chaque enregistrement de cette page.
			siteApi.updateHome({
				promoCards: promos,
				trustItems: trust,
			}),
		onSuccess: (res: HomeContent) => {
			qc.setQueryData(["home-content"], res);
			setDirty(false);
			toast.success("Page d’accueil mise à jour.");
		},
		onError: (e) =>
			toast.error(extractErrorMessage(e, "Enregistrement impossible")),
	});

	const up = <T,>(
		list: T[],
		set: (v: T[]) => void,
		i: number,
		patch: Partial<T>,
	) => {
		marquer();
		set(list.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
	};
	const rm = <T,>(list: T[], set: (v: T[]) => void, i: number) => {
		marquer();
		set(list.filter((_, idx) => idx !== i));
	};
	const push = <T,>(list: T[], set: (v: T[]) => void, item: T) => {
		marquer();
		set([...list, item]);
	};

	const ajouter = (label: string, onClick: () => void) => (
		<AdminButton variant="ghost" onClick={onClick} disabled={!peutEcrire}>
			<AdminIcon n="plus" s={13} /> {label}
		</AdminButton>
	);

	return (
		<>
			<PageHead
				title="Page d’accueil"
				sub="Ce que le visiteur voit en arrivant sur le site"
			/>

			<CmsToolbar
				title="Contenus de l’accueil"
				subtitle={
					peutEcrire
						? "Les trois blocs ci-dessous se suivent de haut en bas sur la page d’accueil."
						: "Lecture seule : la permission « CMS » est requise pour modifier."
				}
				dirty={dirty}
				saving={save.isPending}
				onSave={peutEcrire ? () => save.mutate() : undefined}
				saveLabel="Enregistrer l’accueil"
			/>

			{isLoading ? (
				<div className="card">
					<div className="card-b">
						<span className="muted">Chargement…</span>
					</div>
				</div>
			) : (
				<>
					{/* Le carrousel est passé dans « Bannières » : il y gagne une
					    période, une cible, des chiffres et les tests A/B. Deux écrans
					    qui écrivaient le même contenu, c'était le dernier enregistré
					    qui gagnait, sans que personne ne le sache. */}
					<div style={{ marginBottom: 14 }}>
						<AlertBanner
							variant="info"
							title="Le carrousel se gère désormais dans « Bannières »"
							description="Chaque volet y devient une bannière avec une période de diffusion, une cible et ses propres chiffres."
							actions={
								<Link className="btn btn-secondary btn-sm" to="/marketing/bannieres">
									Ouvrir les bannières
								</Link>
							}
						/>
					</div>

					{/* ─── Cartes promo ─── */}
					<CmsSection
						title="Cartes promotionnelles"
						where="Deux encarts colorés, sous le carrousel"
						icon="tag"
						count={promos.length}
						countLabel={(n) => (n > 1 ? `${n} cartes` : `${n} carte`)}
						action={ajouter("Ajouter une carte", () =>
							push(promos, setPromos, {
								tone: "terra",
								eyebrow: "",
								title: "",
								text: "",
								ctaLabel: "Découvrir",
								href: "/bons-plans",
								big: "",
							}),
						)}
					>
						<CmsPlacement
							zone="cards"
							text="Ces encarts servent à pousser une opération précise : une remise, une gamme, une origine. Au-delà de deux, ils perdent leur force."
						/>
						{promos.map((p, i) => (
							<CmsItem
								key={i}
								index={i}
								label={p.title || "Carte sans titre"}
								onRemove={peutEcrire ? () => rm(promos, setPromos, i) : undefined}
								removeLabel="Supprimer cette carte"
							>
								<CmsGrid>
									<CmsField label="Style" hint="Deux habillages de couleur">
										<select
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.tone}
											onChange={(e) =>
												up(promos, setPromos, i, {
													tone: e.target.value as PromoCard["tone"],
												})
											}
										>
											<option value="terra">Terracotta (orangé)</option>
											<option value="forest">Forêt (vert foncé)</option>
										</select>
									</CmsField>
									<CmsField
										label="Chiffre-clé"
										hint="Affiché en très gros, ex. « −50% » ou « 100% »"
									>
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.big}
											onChange={(e) =>
												up(promos, setPromos, i, { big: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Accroche" hint="Petit texte au-dessus du titre">
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.eyebrow}
											onChange={(e) =>
												up(promos, setPromos, i, { eyebrow: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Titre">
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.title}
											onChange={(e) =>
												up(promos, setPromos, i, { title: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Description" hint="Une ligne, pas davantage" span>
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.text}
											onChange={(e) =>
												up(promos, setPromos, i, { text: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Texte du bouton">
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.ctaLabel}
											onChange={(e) =>
												up(promos, setPromos, i, { ctaLabel: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Destination du bouton" hint="Ex. /bons-plans">
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={p.href}
											onChange={(e) =>
												up(promos, setPromos, i, { href: e.target.value })
											}
										/>
									</CmsField>
								</CmsGrid>
							</CmsItem>
						))}
						{promos.length === 0 && (
							<p className="muted" style={{ fontSize: 12, margin: 0 }}>
								Aucune carte promotionnelle.
							</p>
						)}
					</CmsSection>

					{/* ─── Réassurance ─── */}
					<CmsSection
						title="Bande de réassurance"
						where="Bandeau d’arguments (livraison, paiement, retour) sur l’accueil"
						icon="shield"
						count={trust.length}
						countLabel={(n) => (n > 1 ? `${n} arguments` : `${n} argument`)}
						action={ajouter("Ajouter un argument", () =>
							push(trust, setTrust, { icon: "truck", title: "", subtitle: "" }),
						)}
					>
						<CmsPlacement
							zone="band"
							text="C’est ce qui rassure avant l’achat. Sur un marché où la crainte de l’arnaque est le premier frein, quatre arguments courts valent mieux qu’un long discours."
						/>
						<div
							style={{
								background: "var(--a-bg-2)",
								border: "1px solid var(--a-border)",
								borderRadius: 8,
								padding: "9px 11px",
								marginBottom: 12,
								fontSize: 11.5,
								lineHeight: 1.5,
							}}
						>
							Écrivez un repère plutôt qu’un chiffre : il sera remplacé par la
							valeur réglée dans l’onglet <strong>Engagements</strong>. Un chiffre
							saisi en clair se fige et finira par contredire le reste du site.
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: 6,
									marginTop: 7,
								}}
							>
								{TOKENS.map((tk) => (
									<code
										key={tk}
										style={{
											background: "var(--a-bg)",
											border: "1px solid var(--a-border)",
											borderRadius: 5,
											padding: "2px 6px",
											fontSize: 11,
										}}
									>
										{tk}
									</code>
								))}
							</div>
						</div>
						{trust.map((tr, i) => (
							<CmsItem
								key={i}
								index={i}
								label={tr.title || "Argument sans titre"}
								onRemove={peutEcrire ? () => rm(trust, setTrust, i) : undefined}
								removeLabel="Supprimer cet argument"
							>
								<CmsGrid>
									<CmsField label="Icône">
										<select
											style={cmsInput}
											disabled={!peutEcrire}
											value={tr.icon}
											onChange={(e) =>
												up(trust, setTrust, i, { icon: e.target.value })
											}
										>
											{ICONS.map((ic) => (
												<option key={ic.value} value={ic.value}>
													{ic.label}
												</option>
											))}
										</select>
									</CmsField>
									<CmsField label="Titre" hint="Ex. « Retour {retourJours} jours »">
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={tr.title}
											onChange={(e) =>
												up(trust, setTrust, i, { title: e.target.value })
											}
										/>
									</CmsField>
									<CmsField label="Précision" hint="Une ligne sous le titre" span>
										<input
											style={cmsInput}
											disabled={!peutEcrire}
											value={tr.subtitle}
											onChange={(e) =>
												up(trust, setTrust, i, { subtitle: e.target.value })
											}
										/>
									</CmsField>
								</CmsGrid>
							</CmsItem>
						))}
						{trust.length === 0 && (
							<p className="muted" style={{ fontSize: 12, margin: 0 }}>
								Aucun argument de réassurance.
							</p>
						)}
					</CmsSection>
				</>
			)}
		</>
	);
}
